"use client";
import React from "react";
import Checkbox from "../../../../../components/Checkbox";
import Counter from "../../../../../components/Counter";
import styles from "../../../../global/css/NewScriptForm.module.css";
import "../css/PatientPaymentSection.css";

const patientOptions = ["Smith J (P001)", "Johnson A (P002)", "Brown R (P003)"];
const pharmacistOptions = ["Pharmacist Adams", "Pharmacist Baker", "Pharmacist Carter"];
const practitionerOptions = ["Dr. Adams", "Dr. Baker", "Dr. Carter"];
const paymentOptions = ["Cash", "Medical Aid"];

export default function PatientPaymentSection({
  patient, setPatient,
  pharmacist, setPharmacist,
  practitioner, setPractitioner,
  paymentType = "Cash", setPaymentType,
  scriptType, setScriptType,
  medicalAidNumber, setMedicalAidNumber,
  isChronic, setIsChronic,
  isRepeat, setIsRepeat,
  repeatCount, setRepeatCount,
  // Script number (editable)
  scriptNumber,
  setScriptNumber,
  // Optional file upload handler
  onFileUpload,
  // New optional visibility flags (default to true for backward compatibility)
  showScriptNumber = true,
  showPatient = true,
  showPharmacist = true,
  showPractitioner = true,
  showPaymentType = true,
  showMedicalAidNumber = true,
  showChronic = true,
  showRepeat = true,
  // Optional disabling flags
  disablePatient = false,
  disablePractitioner = false,
  disablePharmacist = false,
  // Optional dynamic options (fallback to defaults for backward compatibility)
  patientOptionsList,
  practitionerOptionsList,
  pharmacistOptionsList,
  // Control fallback behaviour for practitioner options
  useDefaultPractitionerOptions = true,
  paymentTypeReadOnly = false,
  scriptNumberReadOnly = false,
  scriptTypeOptions,
  // Optional document history summary (for amend flows)
  documentCount = 0,
  historyLoading = false,
  historyError = "",
  onShowDocumentHistory,
}) {
  const [uploadedName, setUploadedName] = React.useState("");
  const handleLocalFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setUploadedName(file ? file.name : "");
    if (onFileUpload) onFileUpload(e);
  };
  // Resolve options: prefer provided lists, otherwise use defaults
  const patients = Array.isArray(patientOptionsList) && patientOptionsList.length > 0 ? patientOptionsList : patientOptions;
  const practitioners = Array.isArray(practitionerOptionsList) && practitionerOptionsList.length > 0
    ? practitionerOptionsList
    : (useDefaultPractitionerOptions ? practitionerOptions : []);
  const pharmacists = Array.isArray(pharmacistOptionsList) && pharmacistOptionsList.length > 0 ? pharmacistOptionsList : pharmacistOptions;
  return (
    <div className={styles.cardtop}>
      {/* Top-right document history summary, when available */}
      {documentCount > 0 && (
        <div className={styles.docSummaryContainer}>
          <div className={styles.docSummaryInline}>
            <span className={styles.docSummaryLabel}>Scripts: {documentCount}</span>
            <button
              type="button"
              onClick={onShowDocumentHistory}
              className={styles.docSummaryButton}
            >
              {historyLoading ? "Loading..." : "View History"}
            </button>
          </div>
          {historyError && (
            <div className={styles.docSummaryError}>{historyError}</div>
          )}
        </div>
      )}

      <div className={styles.cardContent}>
        {showScriptNumber && (
          <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="scriptNumber" className={styles.label}>Script Number</label>
            <input
              id="scriptNumber"
              type="text"
              value={scriptNumber}
              onChange={scriptNumberReadOnly ? undefined : (e) => setScriptNumber && setScriptNumber(e.target.value)}
              className={styles.input}
              placeholder="Enter script number"
              readOnly={scriptNumberReadOnly}
            />
          </div>
        )}
        {showPatient && (
          <div className={styles.inputGroup}>
            <label htmlFor="patient" className={styles.label}>Patient</label>
            <input
              id="patient"
              type="text"
              value={patient}
              readOnly
              className={styles.select}
            />
          </div>
        )}

        {showPractitioner && (
          <div className={styles.inputGroup}>
            <label htmlFor="practitioner" className={styles.label}>Practitioner</label>
            <input
              id="practitioner"
              type="text"
              value={practitioner}
              readOnly
              className={styles.select}
            />
          </div>
        )}

        {showPharmacist && (
          <div className={styles.inputGroup}>
            <label htmlFor="pharmacist" className={styles.label}>Pharmacist</label>
            <input
              id="pharmacist"
              type="text"
              value={pharmacist}
              readOnly
              className={styles.select}
            />
          </div>
        )}

        {showPaymentType && (
          <div className={styles.inputGroup}>
            <label htmlFor="paymentType" className={styles.label}>Payment Type</label>
            {paymentTypeReadOnly ? (
              <div className="paymentTypeRow">
                <input
                  id="paymentType"
                  type="text"
                  value={paymentType}
                  readOnly
                  className={styles.select}
                />
                {paymentType === "Cash" && Array.isArray(scriptTypeOptions) && scriptTypeOptions.length > 0 && (
                  <select
                    id="scriptTypeInline"
                    value={scriptType}
                    onChange={(e) => setScriptType && setScriptType(e.target.value)}
                    className={styles.select}
                  >
                    {scriptTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <div className="paymentTypeRow">
                <select
                  id="paymentType"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  className={styles.select}
                >
                  <option value="" disabled hidden>Select Payment Type</option>
                  {paymentOptions.map((option, idx) => (
                    <option key={idx} value={option}>{option}</option>
                  ))}
                </select>
                {paymentType === "Cash" && Array.isArray(scriptTypeOptions) && scriptTypeOptions.length > 0 && (
                  <select
                    id="scriptTypeInline"
                    value={scriptType}
                    onChange={(e) => setScriptType && setScriptType(e.target.value)}
                    className={styles.select}
                  >
                    {scriptTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        )}

        {showMedicalAidNumber && paymentType === "Medical Aid" && (
          <div className={styles.inputGroup}>
            <label htmlFor="medicalAidNumber" className={styles.label}>Medical Aid Number</label>
            <input
              id="medicalAidNumber"
              type="text"
              value={medicalAidNumber}
              readOnly
              className={styles.input}
            />
          </div>
        )}

        {showChronic && (
          <div className={styles.checkboxGroup}>
            <Checkbox
              id="chronic"
              label="Chronic Medication"
              checked={isChronic}
              onChange={(e) => setIsChronic(e.target.checked)}
              className={styles.checkbox}
            />
          </div>
        )}

        {showRepeat && (
          <div className={styles.checkboxGroup}>
            <div className={styles.checkboxCounterWrapper}>
              <Checkbox
                id="repeat"
                label="Repeat Script"
                checked={isRepeat}
                onChange={(e) => setIsRepeat(e.target.checked)}
                className={styles.checkbox}
              />
              {isRepeat && (
                <Counter
                  value={repeatCount}
                  onChange={setRepeatCount}
                  min={0}
                />
              )}
            </div>
          </div>
        )}

        {/* Upload supporting documents */}
        <div className={`${styles.inputGroup} ${styles.mcSmall}`}>
          <div className="fileInputWrapper">
            <label htmlFor="supportingDocs" className="fileInputLabel">Upload Script</label>
            <input
              id="supportingDocs"
              name="supportingDocs"
              type="file"
              accept="image/*,.pdf"
              className="fileInput"
              onChange={handleLocalFileChange}
            />
            <span className="fileName">{uploadedName || "No file chosen"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
